<template>
  <div class="account-object-fields">
    <div class="">
      <label for="isCustomerOf"> Is customer of: </label>
      <BittsSelect
        v-bind="baseSelectProps"
        v-model="isCustomerOf"
        id="isCustomerOf"
        :options="partnerOptions"
        :disabled="isDisabled"
        :error="!!errorLookup.is_customer_of"
        :error-message="errorLookup.is_customer_of ?? ''"
        size="medium"
      />
    </div>
    <div>
      <label for="isOppyFor">Is opportunity for:</label>
      <BittsSelect
        v-bind="baseSelectProps"
        v-model="isOppyFor"
        id="isOppyFor"
        :options="oppyOptions"
        :disabled="isDisabled"
        :error="!!errorLookup.is_opportunity_for"
        :error-message="errorLookup.is_opportunity_for ?? ''"
        size="medium"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsSelect } from '@crossbeam/bitts';

import { computed, inject, ref, watch } from 'vue';

import {
  getFieldErrorMessage,
  useSalesforcePicklistFieldsForPush,
} from '@/composables/useSalesforcePicklistFieldsForPush';
import { ProvidedTrayState } from '@/injectionKeys/integration_profiles';
import { EcosystemCrossbeamField } from '@/types/integration_profiles';

const { disabled } = defineProps<{
  disabled?: boolean;
}>();
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const trayState = inject(ProvidedTrayState)!;

const { allCustomFields, getOptionsForSelect } =
  useSalesforcePicklistFieldsForPush();

const errorLookup = computed(() => ({
  is_customer_of: getFieldErrorMessage(
    isCustomerOf.value,
    allCustomFields.value,
  ),
  is_opportunity_for: getFieldErrorMessage(
    isOppyFor.value,
    allCustomFields.value,
  ),
}));

const isDisabled = computed(
  () =>
    disabled ||
    !trayState?.isEnabled ||
    trayState.accountPush !== 'ALL' ||
    trayState.isSaving,
);

const baseSelectProps = {
  allowClear: true,
  placeholder: 'Map to CRM field',
  class: 'w-300',
};

function setField(field: EcosystemCrossbeamField, value?: string) {
  if (!value) return;

  if (field === 'is_customer_of') isCustomerOf.value = value;
  if (field === 'is_opportunity_for') isOppyFor.value = value;
}

const partnerOptions = computed(() => getOptionsForSelect(isCustomerOf.value));

const oppyOptions = computed(() => getOptionsForSelect(isOppyFor.value));

const isCustomerOf = ref<string | undefined>(undefined);
const isOppyFor = ref<string | undefined>(undefined);

watch(
  () => trayState.ecosystemMaps,
  (mapping) => {
    if (!mapping) return;

    const customerField = mapping.find(
      (f) => f.crossbeam_field === 'is_customer_of',
    );
    const opportunityField = mapping.find(
      (f) => f.crossbeam_field === 'is_opportunity_for',
    );

    setField('is_customer_of', customerField?.crm_field);
    setField('is_opportunity_for', opportunityField?.crm_field);
  },
  { immediate: true },
);

watch(
  [isCustomerOf, isOppyFor],
  ([customerOf, oppyFor]) => {
    if (!customerOf && !oppyFor) {
      trayState.ecosystemMaps = null;
      return;
    }
    trayState.ecosystemMaps = [
      { crossbeam_field: 'is_customer_of', crm_field: customerOf },
      { crossbeam_field: 'is_opportunity_for', crm_field: oppyFor },
    ];
  },
  { immediate: true },
);
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.account-object-fields {
  @apply flex flex-col gap-12;
}
.account-object-fields label {
  @apply block text-neutral-text-strong font-bold text-sm mb-8;
}
</style>
